import { useEffect, useRef } from 'react';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';

/**
 * Tabs — the ONE tab bar, ported from views/components/tabs.ejs + public/tabs.js.
 *
 * Two modes, and the difference is not cosmetic: a bar whose items carry `href` is NAVIGATION and renders
 * as links with aria-current; a bar that swaps panels in place renders as a real tablist with arrow-key
 * movement and aria-selected. In the EJS app public/tabs.js owned that behaviour; here it is the
 * component's, so no page can hand-write half of it.
 */
export type TabItem = {
  key: string;
  label: ReactNode;
  /** Link mode only: where this tab goes. */
  href?: string;
  /** Button mode only: the id of the panel this tab controls. */
  pane?: string;
};

export type TabsProps = {
  items: TabItem[];
  /** The selected key. In link mode, mark the current page's key. */
  active?: string;
  id?: string;
  mode?: 'link' | 'button';
  variant?: 'flat' | 'segmented';
  /** A white thumb on the track. Every bar in this app uses it; the flat tone is kept for the gallery. */
  raised?: boolean;
  ariaLabel?: string;
  /** Markup after the last tab; in a segmented bar it sits BESIDE the track, not inside it. */
  trailing?: ReactNode;
  className?: string;
  /**
   * The bar's own geometry, which a page owns: the EJS component takes it as `attrs.style` and pages pass
   * margins (`4px 0 12px` on Home). A ported page without it sits 16px out of step with its EJS pair.
   */
  style?: CSSProperties;
  /** Button mode: called with the newly selected key. */
  onChange?: (key: string) => void;
};

export function Tabs({
  items,
  active,
  id,
  mode,
  variant = 'flat',
  raised,
  ariaLabel,
  trailing,
  className,
  style,
  onChange,
}: TabsProps) {
  const isLink = (mode ?? (items.some((it) => it.href) ? 'link' : 'button')) === 'link';
  // The other half of the ARIA: public/tabs.js marks every pane a bar controls as a tabpanel and points it back at
  // its tab. The SPA set `aria-controls` on the tab and nothing on the panel, so a panel announced itself as a group
  // with no name. Only the LINK between the two is written here — the page still decides what a panel contains and
  // whether it is hidden, exactly as views/components/tabs.ejs leaves that to the page.
  useEffect(() => {
    if (isLink) return;
    for (const it of items) {
      if (!it.pane || !id) continue;
      const paneEl = document.getElementById(it.pane);
      if (!paneEl) continue;
      if (paneEl.getAttribute('role') !== 'tabpanel') paneEl.setAttribute('role', 'tabpanel');
      const label = `${id}-tab-${it.key}`;
      if (paneEl.getAttribute('aria-labelledby') !== label) paneEl.setAttribute('aria-labelledby', label);
    }
  });
  const current = active ?? items[0]?.key;
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Arrow keys move the selection in button mode, wrapping at both ends — the behaviour public/tabs.js
  // provided for EJS, so a keyboard user gets the same thing on both front ends.
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (isLink) return;
    const i = items.findIndex((it) => it.key === current);
    if (i < 0) return;
    // Home and End jump to the ends, as ARIA's tab pattern says: a long bar should not need N arrow presses.
    if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      const next = items[e.key === 'Home' ? 0 : items.length - 1];
      onChange?.(next.key);
      refs.current[next.key]?.focus();
      return;
    }
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = items[(i + step + items.length) % items.length];
    onChange?.(next.key);
    refs.current[next.key]?.focus();
  };

  const barClass =
    'tabs' +
    (variant === 'segmented' ? ' tabs-segmented' : '') +
    (variant === 'segmented' && raised ? ' tabs-raised' : '') +
    (className ? ` ${className}` : '');

  const bar = (
    <div
      className={barClass}
      id={id}
      role={isLink ? 'navigation' : 'tablist'}
      aria-label={ariaLabel}
      style={variant === 'segmented' && trailing ? undefined : style}
      onKeyDown={onKeyDown}
    >
      {items.map((it) => {
        const on = it.key === current;
        if (isLink) {
          return (
            <a
              key={it.key}
              className={on ? 'tab t-dense active' : 'tab t-dense'}
              href={it.href}
              aria-current={on ? 'page' : undefined}
            >
              {it.label}
            </a>
          );
        }
        return (
          <button
            key={it.key}
            ref={(el) => {
              refs.current[it.key] = el;
            }}
            type="button"
            className={on ? 'tab t-dense active' : 'tab t-dense'}
            id={id ? `${id}-tab-${it.key}` : undefined}
            data-tab-key={it.key}
            aria-controls={it.pane}
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange?.(it.key)}
          >
            {it.label}
          </button>
        );
      })}
      {variant === 'segmented' && trailing ? null : trailing}
    </div>
  );

  if (variant === 'segmented' && trailing) {
    return (
      <div className="tabs-row" style={style}>
        {bar}
        {trailing}
      </div>
    );
  }
  return bar;
}
